import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

function ForceChangePassword() {
  const { user, clearMustChangePassword, logout, replaceToken } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!user || !user.must_change_password) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (currentPassword === newPassword) {
      setError('New password must be different from current password');
      return;
    }

    setLoading(true);
    try {
      const r = await axios.post('/api/auth/change-password', {
        currentPassword,
        newPassword,
      });
      replaceToken(r.data && r.data.token); // older logins end with the change; keep this browser signed in
      clearMustChangePassword();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)',
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      fontFamily: 'var(--font-sans)',
    }}>
      <div style={{
        background: 'var(--bg-card)', borderRadius: 16,
        border: '1px solid var(--border-subtle)',
        boxShadow: 'var(--shadow-lg)',
        padding: '2.5rem', maxWidth: 420, width: '90%',
        animation: 'fadeInUp 0.3s ease-out',
      }}>
        {/* Icon */}
        <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 56, height: 56, borderRadius: 14,
            background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.25)',
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h2 style={{
          margin: '0 0 0.35rem', textAlign: 'center',
          fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-primary)',
        }}>
          Password Change Required
        </h2>
        <p style={{
          margin: '0 0 1.5rem', textAlign: 'center',
          fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.5,
        }}>
          {user.username}, you must set a new password before continuing.
        </p>

        {/* Error */}
        {error && (
          <div style={{
            padding: '0.55rem 0.75rem', borderRadius: 8, marginBottom: '1rem',
            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)',
            color: '#f87171', fontSize: '0.78rem', fontWeight: 500,
          }}>
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>
              Current Password
            </label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter your current password"
              autoComplete="current-password"
              autoFocus
              style={{
                width: '100%', padding: '0.6rem 0.75rem', borderRadius: 8,
                background: 'var(--bg-input)', border: '1px solid var(--border-default)',
                color: 'var(--text-primary)', fontSize: '0.85rem', outline: 'none',
                transition: 'border-color 0.15s', boxSizing: 'border-box',
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--accent-blue)'}
              onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>
              New Password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="At least 6 characters"
              autoComplete="new-password"
              style={{
                width: '100%', padding: '0.6rem 0.75rem', borderRadius: 8,
                background: 'var(--bg-input)', border: '1px solid var(--border-default)',
                color: 'var(--text-primary)', fontSize: '0.85rem', outline: 'none',
                transition: 'border-color 0.15s', boxSizing: 'border-box',
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--accent-blue)'}
              onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter new password"
              autoComplete="new-password"
              style={{
                width: '100%', padding: '0.6rem 0.75rem', borderRadius: 8,
                background: 'var(--bg-input)', border: '1px solid var(--border-default)',
                color: 'var(--text-primary)', fontSize: '0.85rem', outline: 'none',
                transition: 'border-color 0.15s', boxSizing: 'border-box',
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--accent-blue)'}
              onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              marginTop: '0.5rem', padding: '0.65rem', borderRadius: 8,
              cursor: loading ? 'wait' : 'pointer', fontWeight: 700, fontSize: '0.85rem',
              background: 'var(--accent-blue)',
              border: 'none', color: '#fff',
              transition: 'all 0.2s', letterSpacing: '0.02em',
            }}
            onMouseOver={(e) => { if (!loading) e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            {loading ? 'Updating...' : 'Change Password'}
          </button>

          <button
            type="button"
            onClick={logout}
            style={{
              padding: '0.45rem', borderRadius: 8,
              cursor: 'pointer', fontWeight: 600, fontSize: '0.75rem',
              background: 'transparent', border: '1px solid rgba(255,255,255,0.08)',
              color: 'var(--text-muted)', transition: 'all 0.15s',
            }}
            onMouseOver={(e) => e.currentTarget.style.color = '#94a3b8'}
            onMouseOut={(e) => e.currentTarget.style.color = '#64748b'}
          >
            Logout Instead
          </button>
        </form>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default ForceChangePassword;
